import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import aggridUtils from "../../../../utils/aggrid-utils.js";
import {useQuery} from "@tanstack/react-query";
import {navigateHook} from "../../../../hooks/use-navigate.js";
import {Template} from "../../../../components/template/template.jsx";
import Button from "../../../../components/button/button.jsx";
import {FaEraser, FaPaste, FaPlus, FaSearch} from "react-icons/fa";
import {Card} from "../../../../components/card/card.jsx";
import {AgGridReact} from "ag-grid-react";
import {customFormatDate, DEFAULT_COL_DEF_NO_RESIZE_SUPPRESS_ALL} from "../../../../utils/ag-grid-columns-utils.js";
import WorkScheduleService from "../../../../services/web/work-schedule-service.js";
import InputForm from "../../../../components/input/input-form.jsx";
import InputDateTime from "../../../../components/input/input-datetime.jsx";
import {Modal} from "../../../../components/modal/modal.jsx";
import {add, format} from "date-fns";
import PeriodInput from "../../../../components/input/period-input.jsx";

const initialFilter = {
    startPeriod: null,
    endPeriod: null,
    name: ""
}

const WorkScheduleList = () => {
    const [filter, setFilter] = useState(initialFilter);
    const [gridApi, setGridApi] = useState(null);
    const gridRef = useRef(null);
    const [copiedScale, setCopiedScale] = useState({});
    const [copyScale, setCopyScale] = useState(initialFilter);
    const [showCopyModal, setShowCopyModal] = useState(false);
    const columnDefs = useMemo(() => [
        {field: "id", hide: true},
        {field: "name", headerName: "Nome", flex: 5, filter: "agTextColumnFilter", filterParams: aggridUtils.filterParams,
            comparator: (a, b, nodeA, nodeB, isInverted) => {
                return aggridUtils.nullLastComparator(a, b, nodeA, nodeB, isInverted);
            }},
        {field: "status", headerName: "Status", flex: 5, filter: "agTextColumnFilter", filterParams: aggridUtils.filterParams,
            valueFormatter: params => params.value === "PENDING" ? "Pendente" : "Finalizada"},
        {field: "startPeriod", headerName: "Data inicial", flex: 5, filter: "agDateColumnFilter",
            valueFormatter: params => customFormatDate(params.value)},
        {field: "endPeriod", headerName: "Data final", flex: 5, filter: "agDateColumnFilter",
            valueFormatter: params => customFormatDate(params.value)},
        { field: "copy", headerName: "", flex: 1,
            cellRenderer: (params) => {
                const workScheduleData = params.data;
                return (
                    <div className="flex items-center w-full justify-center h-full">
                        <Button title="Copiar escala" disabled={!workScheduleData.teams} onClick={(e) => {
                            setCopiedScale(workScheduleData);
                            setShowCopyModal(true);
                        }}>
                            <FaPaste/>
                        </Button>
                    </div>);
            }}
    ], []);

    const defaultColDef = DEFAULT_COL_DEF_NO_RESIZE_SUPPRESS_ALL;

    const navigate = navigateHook();

    const { data: workSchedules = [], isLoading, refetch: refetchList } = useQuery({
        queryKey: ['workSchedules'],
        queryFn: async () => {
            const { data } = await WorkScheduleService.list();
            return data;
        },
    });

    const onChange = useCallback(({ target: { id, value } }) => {
        setFilter(prev => ({ ...prev, [id]: value }));
    }, []);

    const handleDateCopy = useCallback((value, field) => {
        setCopyScale(prev => ({
            ...prev,
            [field]: value || null
        }));
    }, []);

    const formatDateForAgGrid = useCallback(date => {
        if (!date) return null;
        const d = new Date(date);
        return d.toISOString().split("T")[0];
    }, []);

    const search = useCallback(() => {
        if (!gridApi) return;

        const filterModel = {};

        Object.entries(filter).forEach(([field, value]) => {
            if (!value) return;

            if (["startPeriod", "endPeriod"].includes(field)) {
                const dateString = formatDateForAgGrid(value);
                const isStart = field === "startPeriod";

                filterModel[field] = {
                    filterType: "date",
                    operator: "OR",
                    conditions: [
                        { type: isStart ? "greaterThan" : "lessThan", dateFrom: dateString },
                        { type: "equals", dateFrom: dateString }
                    ]
                };
            } else {
                filterModel[field] = {
                    filterType: "text",
                    type: "contains",
                    filter: value
                };
            }
        });

        gridApi.setFilterModel(filterModel);
        gridApi.onFilterChanged();
    }, [filter, gridApi]);

    const clearFilter = useCallback(() => {
        setFilter(initialFilter);
        const api = gridRef.current?.api;
        if (api) {
            api.setFilterModel(null);
            api.onFilterChanged();
        }
    }, []);

    useEffect(() => {
        const handleWindowKeyDown = (e) => {
            if (e.key === 'Enter') {
                search();
            }
        };

        window.addEventListener('keydown', handleWindowKeyDown);

        return () => {
            window.removeEventListener('keydown', handleWindowKeyDown);
        };
    }, [search]);

    const onRowClicked = (event) => {
        const {id, status}= event.data;
        if(status === "PENDING") {
            navigate(`/work-schedule/conflicts/${id}`, true)
        } else {
            navigate(`/work-schedule/form/${id}`, true)
        }
    }

    const openForm = () => {
        navigate( `/work-schedule/form/`, false)
    }

    const handleCloseCopyModal = () => {
        setShowCopyModal(false);
        setCopyScale(initialFilter)
    }

    const handleCopy = () => {
        const urlDev = `/work-schedule/form?cp=${copiedScale.id}&dtStart=${format(copyScale.startPeriod,  "yyyy-MM-dd'T'12:00:00")}&dtEnd=${format(copyScale.endPeriod, "yyyy-MM-dd'T'12:00:00")}&name=${copyScale.name}`
        navigate(urlDev, true)
        handleCloseCopyModal();
    }

    return (
        <>
            <Template isLoading={isLoading}>
                <Template.Header>
                    <Template.Title>
                        Escalas
                    </Template.Title>
                    <Button onClick={openForm}>
                        <FaPlus/><span className="font-bold">Adicionar escala</span>
                    </Button>
                </Template.Header>
                <Template.Body>
                    <Card>
                        <Card.Body>
                            <div className="grid grid-cols-3 gap-4">
                                <PeriodInput
                                    startDateId={"startPeriod"}
                                    endDateId={"endPeriod"}
                                    startDate={filter.startPeriod}
                                    endDate={filter.endPeriod}
                                    setFilter={setFilter}
                                    cols={1}
                                />

                                <div className="col-span-2 flex gap-4">
                                    <div className="w-full">
                                        <InputForm id="name" name="name" value={filter.name} onChange={onChange} label="Nome"/>
                                    </div>

                                    <div className="flex gap-2">
                                        <Button target="primary-outline" onClick={clearFilter} title="Limpar">
                                            <FaEraser/>
                                        </Button>
                                        <Button onClick={search} title="Buscar"><FaSearch/></Button>
                                    </div>
                                </div>
                            </div>
                        </Card.Body>
                    </Card>
                    <Card className="mt-2">
                        <Card.Body>
                            <div className={"h-[calc(100vh-153px)] aggrid-row-pointer"}>
                                <AgGridReact
                                    onGridReady={(params) => {
                                        setGridApi(params.api);
                                    }}
                                    onFilterChanged={aggridUtils.filterChangedNoRowsOverlay}
                                    ref={gridRef}
                                    rowData={workSchedules}
                                    columnDefs={columnDefs}
                                    defaultColDef={defaultColDef}
                                    onCellClicked={(e) => {
                                        const {column} = e;
                                        if(column.colId === "copy"){
                                            e.stopPropagation();
                                    }}}
                                    onRowClicked={onRowClicked}
                                    pagination
                                    paginationPageSize={50}
                                    paginationPageSizeSelector={[20, 50, 100, 500]}
                                    animateRows={false}
                                    suppressCellFocus={true}
                                    gridOptions={{localeText: aggridUtils.localization()}}
                                    theme={aggridUtils.generalTheme()}
                                />
                            </div>
                        </Card.Body>
                    </Card>
                    <Modal open={showCopyModal} setOpen={handleCloseCopyModal} title="Copiar a escala" className="w-5/12" overflow={true}>
                        <Modal.Body>
                            <span className="font-bold">De</span>
                            <div className="flex gap-2 my-2">
                                <div className="flex-2">
                                    <InputForm label="Nome" disabled={true} value={copiedScale.name ?? ""}/>
                                </div>
                                <div className="flex-1">
                                    <InputDateTime
                                        id="startPeriodCopied"
                                        label="Data inicial"
                                        name="startPeriodCopied"
                                        dateFormat="L"
                                        disabled={true}
                                        selected={copiedScale.startPeriod ? new Date(copiedScale.startPeriod) : null}
                                        onChange={() => {}}
                                    />
                                </div>
                                <div className="flex-1">
                                    <InputDateTime
                                        id="endPeriodCopied"
                                        label="Data final"
                                        name="endPeriodCopied"
                                        dateFormat="L"
                                        disabled={true}
                                        selected={copiedScale.endPeriod ? new Date(copiedScale.endPeriod) : null}
                                        onChange={() => {}}
                                    />
                                </div>
                            </div>
                            <span className="font-bold">Para</span>
                            <div className="flex gap-2 mt-2">
                                <div className="flex-2">
                                    <InputForm label="Nome" id="modal_name" value={copyScale.name}
                                               onChange={({target}) => setCopyScale(prevState => ({...prevState, name: target.value}))}/>
                                </div>
                                <div className="flex-1">
                                    <InputDateTime
                                        id="startPeriodCopy"
                                        label="Data inicial *"
                                        name="startPeriodCopy"
                                        dateFormat="L"
                                        selected={copyScale.startPeriod}
                                        onChange={(value) => handleDateCopy(value, "startPeriod")}
                                        restrictions={{before: new Date()}}
                                    />
                                </div>
                                <div className="flex-1">
                                    <InputDateTime
                                        id="endPeriodCopy"
                                        label="Data final *"
                                        name="endPeriodCopy"
                                        dateFormat="L"
                                        selected={copyScale.endPeriod}
                                        onChange={(value) => handleDateCopy(value, "endPeriod")}
                                        disabled={!copyScale.startPeriod}
                                        restrictions={{before: copyScale.startPeriod? add(copyScale.startPeriod, {days: 1}): undefined}}
                                    />
                                </div>
                            </div>
                        </Modal.Body>
                        <Modal.Footer className="flex gap-2">
                            <Button onClick={handleCopy} disabled={!(copyScale.startPeriod && copyScale.endPeriod)}>Copiar</Button>
                        </Modal.Footer>
                    </Modal>
                </Template.Body>
            </Template>
        </>
    )
}

export default WorkScheduleList;